import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { EntryTypeStatus, Optional } from 'chaika-frontend-types/frontend-cms';

import { SelectButtonMenuItem } from '@components/SelectButtonMenu';
import { EditEntryTypePageAttributes } from 'chaika-test-attributes';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { translate } from '@chaika/i18n/helper';

import { EntryTypeDraft } from '../../../types';
import { useEntryTypeDraft } from '../../../hooks/useEntryTypeDraft';
import { editEntryTypeQueryOptions } from '../../../queries/editEntryTypeQueries';
import { useCreateEntryTypeVersion, usePublishEntryType } from '../../../queries/editEntryTypeMutations';
import { makeEntryTypeSaveParams } from '../../../utils/entryTypeSaveParams';

interface Params {
    entryTypeId: string;
    version: number;
}

interface Data {
    title: string;
    items: SelectButtonMenuItem[];
    onClick(): Promise<void>;
}

export function useEntryTypeHeaderActions({ entryTypeId, version }: Params): Data {
    const navigate = useNavigate();
    const { entryType } = useEntryTypeDraft({ entryTypeId, version });

    const publishMutation = usePublishEntryType();
    const createVersionMutation = useCreateEntryTypeVersion();

    const openVersion = (entryTypeVersion: Optional<number>): void => {
        navigate(`/template/entry-type/${entryTypeId}/version/${entryTypeVersion}`);
    };

    const openDraftVersion = async (activeEntryType: EntryTypeDraft): Promise<void> => {
        const draftVersion = await findDraftVersion(entryTypeId);

        if (draftVersion) {
            openVersion(draftVersion);

            return;
        }

        await createVersionMutation.mutateAsync({ entryTypeId, params: makeEntryTypeSaveParams(activeEntryType) });

        openVersion(await findDraftVersion(entryTypeId));
    };

    const onClick = async (): Promise<void> => {
        if (!entryType) {
            return;
        }

        store.app.setIsOnUpdate(true);

        if (entryType.status === EntryTypeStatus.Active) {
            await openDraftVersion(entryType);
        } else {
            await publishMutation.mutateAsync({ entryTypeId, version });
        }

        store.app.setIsOnUpdate(false);
    };

    const onCreateCopyClick = async (): Promise<void> => {
        if (!entryType) {
            return;
        }

        store.app.setIsOnUpdate(true);

        const copyId = uuid();

        await createVersionMutation.mutateAsync({ entryTypeId: copyId, params: makeEntryTypeSaveParams(entryType) });
        // FIXMI БЕК Не работает, копирование удаляет секции оригинального типа записи
        // await EntryTypeApi.copy(entryType.id, entryType.version ?? 1, copyId);

        navigate(`/template/entry-type/${copyId}/version/1`);

        store.app.setIsOnUpdate(false);
    };

    return {
        title: getActionTitle(entryType?.status),
        items: [
            {
                label: translate('createCopy'),
                dataTest: EditEntryTypePageAttributes.CreateCopyButton,
                onClick: onCreateCopyClick,
            },
        ],
        onClick,
    };
}

async function findDraftVersion(entryTypeId: string): Promise<Optional<number>> {
    const entryTypes = await queryClient.fetchQuery({ ...editEntryTypeQueryOptions.entryTypes(), staleTime: 0 });

    return entryTypes.find(entryType => entryType.id === entryTypeId && entryType.status === EntryTypeStatus.Draft)
        ?.version;
}

function getActionTitle(status: Optional<EntryTypeStatus>): string {
    switch (status) {
        case EntryTypeStatus.Active:
            return translate('edit');
        case EntryTypeStatus.Draft:
            return translate('publish');
        case EntryTypeStatus.Archived:
            return translate('restore');
        default:
            return '';
    }
}
